export type PromptLine = { top: number; indexes: number[] };

/**
 * Measure reading lines by their native text baseline, not a glyph's top edge.
 * Large and small words on the same line have different tops. Native canvas font
 * ascent gives their shared baseline without touching the live text or affecting
 * wrapping. Cache metrics per font and measure only when the layout changes.
 */
export function measurePromptLines(root: HTMLElement): {
  lines: PromptLine[];
  nodes: Map<number, HTMLElement[]>;
} {
  const nodes = new Map<number, HTMLElement[]>();
  const context = document.createElement("canvas").getContext("2d");
  const ascents = new Map<string, number>();
  const lines: PromptLine[] = [];
  const firstLine = new Map<number, { baseline: number; line: PromptLine }>();
  let current: { baseline: number; line: PromptLine } | undefined;
  for (const element of root.querySelectorAll<HTMLElement>("[data-word-index]")) {
    const index = Number(element.dataset.wordIndex);
    const fragments = nodes.get(index);
    if (fragments) fragments.push(element);
    else nodes.set(index, [element]);
    const style = getComputedStyle(element);
    const font = style.font || `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    let ascent = ascents.get(font);
    if (ascent === undefined) {
      if (context) context.font = font;
      const nativeAscent = context?.measureText("Mg").fontBoundingBoxAscent;
      ascent = nativeAscent !== undefined && Number.isFinite(nativeAscent)
        ? nativeAscent
        : parseFloat(style.fontSize) * 0.8;
      ascents.set(font, ascent);
    }
    const top = element.offsetTop;
    const baseline = top + ascent;
    const existing = firstLine.get(index);
    if (existing) {
      // A split formatted word is one reading position. Use its highest glyph
      // on its first line, even when a later fragment has a larger font size.
      if (Math.abs(existing.baseline - baseline) <= 2)
        existing.line.top = Math.min(existing.line.top, top);
      continue;
    }
    // offsetTop and native font metrics round independently; WebKit can differ
    // by one pixel between differently sized glyphs on the very same baseline.
    if (!current || Math.abs(current.baseline - baseline) > 2) {
      current = { baseline, line: { top, indexes: [] } };
      lines.push(current.line);
    } else current.line.top = Math.min(current.line.top, top);
    current.line.indexes.push(index);
    firstLine.set(index, current);
  }
  // Keep text order, including RTL and overlapping glyph boxes at tight line
  // spacing. A later line must never send constant playback backwards.
  let previousTop = -Infinity;
  for (const line of lines) {
    line.top = Math.max(previousTop, line.top);
    previousTop = line.top;
  }
  return { lines, nodes };
}
